import type { ConsultedDocRef, PersistedCitationRef } from "@/types";

// ADR-123 D4 — retrieval provenance for a zero-citation answer.
//
// Deliberately inert, because "a bibliography under a fabricated figure misleads
// just as effectively as a false citation, only more cheaply". This is a flat,
// dashed text list BELOW the answer — no number pill, no count, no thumbnail, no
// hover, no click target, and an explicit disclaimer that these documents were
// read, not verified. That contrast was originally drawn against the Sources card
// (raised numbered clickable cards with page images); that card was deleted
// 2026-08-09, so the surface this must NOT be mistaken for is now the inline
// citation pill, which IS clickable and opens a verified source.
//
// It renders ONLY when there are zero citations, and it was a SIBLING of the
// Sources card rather than a branch inside it — that card returned null on an
// empty citation list, which is precisely the state this component exists for.
// The zero-citation guard below is therefore load-bearing on its own, not a
// leftover of that pairing.

interface DocumentsConsultedProps {
  documents: ConsultedDocRef[] | undefined;
  citations: PersistedCitationRef[];
}

export function DocumentsConsulted({ documents, citations }: DocumentsConsultedProps) {
  if (citations.length > 0) return null;
  if (!documents?.length) return null;

  return (
    <section
      aria-label="Documents consulted"
      className="mt-[var(--space-6)] rounded-[var(--radius-md)] border border-dashed border-[var(--border-default)] bg-[var(--surface-sunken)] px-[var(--space-4)] py-[var(--space-3)]"
    >
      <h3 className="type-overline text-[var(--text-tertiary)]">Documents consulted</h3>
      <ul className="mt-[var(--space-2)] flex flex-col gap-[var(--space-1)]">
        {documents.map((d) => (
          <li key={d.doc_id} className="flex items-start gap-[var(--space-2)]">
            <svg
              width="13"
              height="13"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="mt-[3px] flex-shrink-0 text-[var(--text-tertiary)]"
            >
              <path d="M3 1.5h5L11 4.5v8H3z" />
              <path d="M8 1.5v3h3" />
            </svg>
            <span className="type-body-sm break-words text-[var(--text-secondary)]" title={d.doc_id}>
              {d.doc_title}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-[var(--space-2)] type-caption text-[var(--text-tertiary)]">
        Read while researching this question — not verified as supporting the answer above.
      </p>
    </section>
  );
}
